import React, { useState } from 'react';
import { CreditCardIcon, EllipsisVerticalIcon, EyeIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Payment } from '../../types';
import { BottomSheet } from '../ui/BottomSheet';

export interface CobrosMobilePaymentCardProps {
  payment: Payment;
  formatMoney: (n: number) => string;
  methodLabel: (method: string) => string;
  methodClass: (method: string) => string;
  statusLabel: (status: string) => string;
  statusClass: (status: string) => string;
  onView: () => void;
  onEdit?: () => void;
  onReceipt?: () => void;
  onVerify?: () => void;
  onDelete?: () => void;
  canEdit: boolean;
  canVerify: boolean;
  canDelete: boolean;
}

export const CobrosMobilePaymentCard: React.FC<CobrosMobilePaymentCardProps> = ({
  payment,
  formatMoney,
  methodLabel,
  methodClass,
  statusLabel,
  statusClass,
  onView,
  onEdit,
  onReceipt,
  onVerify,
  onDelete,
  canEdit,
  canVerify,
  canDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const amount =
    payment.amountMinor != null ? payment.amountMinor / 100 : Number(payment.amount || 0);
  const status = payment.status || (payment.isVerified ? 'CONFIRMED' : 'PENDING');
  const date = payment.paymentDate
    ? new Date(payment.paymentDate).toLocaleDateString('es', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';
  const clientName =
    payment.client?.name || (payment.clientId ? `Cliente #${payment.clientId}` : 'N/A');
  const cedula = payment.client?.cedula || '—';
  const allocCount = payment.allocations?.length || 0;

  const run = (fn?: () => void) => {
    setMenuOpen(false);
    fn?.();
  };

  return (
    <>
      <article className="rounded-2xl border border-[#E5EAF1] bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-150 active:scale-[0.99]">
        <div className="flex items-start gap-2.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
            <CreditCardIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="truncate text-sm font-semibold text-slate-900">{clientName}</p>
              <span
                className={clsx(
                  'shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1',
                  statusClass(status)
                )}
              >
                {statusLabel(status)}
              </span>
            </div>
            <p className="mt-0.5 truncate text-xs text-slate-500">{cedula}</p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 border-t border-[#E5EAF1] pt-2.5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Monto</p>
            <p className="mt-0.5 text-xs font-bold tabular-nums text-slate-900">
              {formatMoney(amount)}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Método</p>
            <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-800">
              {methodLabel(payment.paymentMethod)}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Fecha</p>
            <p className="mt-0.5 text-xs font-semibold text-slate-900">{date}</p>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-semibold', methodClass(payment.paymentMethod))}>
            {allocCount > 0 ? `${allocCount} factura(s)` : 'Sin asignación'}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onView}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#E5EAF1] text-slate-600"
              aria-label="Ver pago"
            >
              <EyeIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#E5EAF1] text-slate-600"
              aria-label="Más acciones"
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </article>

      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Acciones">
        <div className="space-y-0.5 pb-2">
          <MenuBtn label="Ver pago" onClick={() => run(onView)} />
          {canEdit && onEdit ? <MenuBtn label="Editar" onClick={() => run(onEdit)} /> : null}
          {onReceipt ? <MenuBtn label="Descargar recibo" onClick={() => run(onReceipt)} /> : null}
          {canVerify && onVerify ? (
            <MenuBtn label="Verificar" onClick={() => run(onVerify)} />
          ) : null}
          {canDelete && onDelete ? (
            <>
              <div className="my-2 border-t border-slate-100" />
              <MenuBtn label="Anular" danger onClick={() => run(onDelete)} />
            </>
          ) : null}
        </div>
      </BottomSheet>
    </>
  );
};

const MenuBtn: React.FC<{
  label: string;
  onClick: () => void;
  danger?: boolean;
}> = ({ label, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'flex w-full items-center rounded-xl px-3 py-3 text-left text-sm font-medium',
      danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-800 hover:bg-slate-50'
    )}
  >
    {label}
  </button>
);

export default CobrosMobilePaymentCard;
